import React, { useState, useEffect } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { Button } from '../ui/Button'
import { adminApi } from '../../services/api/admin'
import { setAccessToken } from '../../services/api/client'

interface ImpersonationClaims {
  sessionId: string
  targetEmail: string
  orgName: string
}

function parseImpersonationClaims(token: string): ImpersonationClaims | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    const sessionId = decoded?.app_metadata?.impersonation_session_id
    if (!sessionId) return null
    return {
      sessionId,
      targetEmail: decoded.email || '',
      orgName: decoded?.app_metadata?.org_name || '',
    }
  } catch {
    return null
  }
}

export function reloadPage(): void {
  window.location.reload()
}

export const ImpersonationBanner: React.FC = () => {
  const [claims, setClaims] = useState<ImpersonationClaims | null>(null)
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) return
    const parsed = parseImpersonationClaims(token)
    setClaims(parsed)
  }, [])

  if (!claims) return null

  const handleExit = async () => {
    setExiting(true)
    try {
      const result = await adminApi.endImpersonation()
      setAccessToken(result.jwt)
      reloadPage()
    } catch {
      setExiting(false)
    }
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="sticky top-0 z-[10100] w-full bg-yellow-50 dark:bg-yellow-900/30 border-b border-yellow-200 dark:border-yellow-700"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span className="text-sm font-medium">
            Impersonating{' '}
            <strong className="font-semibold">{claims.targetEmail}</strong>
            {claims.orgName && (
              <> as part of <strong className="font-semibold">{claims.orgName}</strong></>
            )}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExit}
          disabled={exiting}
          className="flex-shrink-0 border-yellow-400 dark:border-yellow-600 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-800/40"
        >
          <X className="h-4 w-4 mr-1" aria-hidden="true" />
          {exiting ? 'Exiting...' : 'Exit'}
        </Button>
      </div>
    </div>
  )
}
